/**
 * Создаёт миниатюру из Blob изображения.
 * @param {Blob} blob — исходное изображение
 * @param {number} maxWidth
 * @param {number} maxHeight
 * @param {number} quality — JPEG quality (0..1)
 * @returns {Promise<Blob>} маленький JPEG blob
 */
export const generateThumbnail = (blob, maxWidth = 400, maxHeight = 530, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Thumbnail toBlob failed'))),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Не удалось загрузить изображение для миниатюры')); };
    img.src = url;
  });

/**
 * Захватить первый кадр видео как миниатюру.
 * @param {string} videoUrl — URL видео
 * @returns {Promise<Blob>} JPEG blob кадра
 */
// Внешние (часто гео-заблокированные) видео-хосты гоним через серверный прокси,
// иначе кадр не захватится у пользователей без VPN и canvas будет tainted.
const proxifyForFrame = (url) => {
  if (!url) return url;
  if (url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  try {
    const base = typeof window !== 'undefined' ? window.location.href : 'https://gatsby.kluchik.io';
    const u = new URL(url, base);
    if (typeof window !== 'undefined' && u.host === window.location.host) return url;
  } catch (_) {}
  return `/api/proxy-video?url=${encodeURIComponent(url)}`;
};

export const captureVideoFrame = (videoUrl) =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.preload = 'auto';

    const cleanup = () => {
      video.removeAttribute('src');
      video.load();
    };

    video.onloadeddata = () => {
      video.currentTime = 0.5;
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        const scale = Math.min(150 / video.videoWidth, 200 / video.videoHeight, 1);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (b) => { cleanup(); b ? resolve(b) : reject(new Error('Video frame toBlob failed')); },
          'image/jpeg',
          0.7
        );
      } catch (e) {
        cleanup();
        reject(e);
      }
    };

    video.onerror = () => { cleanup(); reject(new Error('Не удалось загрузить видео для миниатюры')); };

    // Таймаут на случай если видео не загрузится
    const timeout = setTimeout(() => { cleanup(); reject(new Error('Таймаут захвата кадра видео')); }, 15000);
    const origResolve = resolve;
    resolve = (val) => { clearTimeout(timeout); origResolve(val); };
    const origReject = reject;
    reject = (err) => { clearTimeout(timeout); origReject(err); };

    video.src = proxifyForFrame(videoUrl);
  });
